import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Shield, Loader2, CheckCircle2, AlertTriangle, AlertOctagon } from "lucide-react";

const EMOTIONS = ["CONFIDENT", "FEARFUL", "FOMO", "GREEDY", "NEUTRAL"];
const DERIVATIVES = ["OPTIONS", "FUTURES", "F&O"];
const toNum = (v) => (v === "" || v == null ? null : Number(v));

const empty = {
  broker: "SHOONYA",
  category: "DELIVERY",
  stock: "",
  exchange: "NS",
  transaction: "BOUGHT",
  rate: "",
  quantity: "",
  lots: "",
  lot_size: "",
  stop_loss: "",
  target: "",
  date_of_purchase: new Date().toISOString().slice(0, 10),
  status: "HOLDING",
  exit_rate: "",
  exit_date: "",
  reason: "",
  notes: "",
  emotion: "",
};

export default function TradeDialog({ open, onClose, onSaved, initial, reference }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [liveLtp, setLiveLtp] = useState(null);
  const [ltpLoading, setLtpLoading] = useState(false);
  const [precheck, setPrecheck] = useState(null);
  const [prechecking, setPrechecking] = useState(false);
  const [lotPresets, setLotPresets] = useState(null);
  const [lotHint, setLotHint] = useState("");

  useEffect(() => {
    if (open) {
      setErr("");
      setLiveLtp(null);
      setPrecheck(null);
      setLotHint("");
      setForm(initial ? { ...empty, ...initial } : empty);
      api.get("/lot-sizes").then((r) => setLotPresets(r.data)).catch(() => setLotPresets(null));
    }
  }, [open, initial]);

  // Auto-fill Lot Size from presets / past trades when symbol changes on a derivative trade
  useEffect(() => {
    if (!open || !lotPresets || !DERIVATIVES.includes(form.category)) return;
    const sym = (form.stock || "").trim().toUpperCase();
    const size = lotPresets.sizes?.[sym];
    if (!size) { if (lotHint) setLotHint(""); return; }
    if (form.lot_size !== "" && lotHint === "") return;
    if (String(form.lot_size) !== String(size)) setForm((f) => ({ ...f, lot_size: String(size) }));
    setLotHint(lotPresets.source?.[sym] === "history" ? `from your last ${sym} trade` : `${sym} preset`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lotPresets, form.stock, form.category]);

  // Auto-fetch LTP whenever stock symbol + exchange change (debounced)
  useEffect(() => {
    if (!open) return;
    const stock = (form.stock || "").trim().toUpperCase();
    if (stock.length < 2) { setLiveLtp(null); return; }
    setLtpLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/prices/quote`, { params: { symbol: stock, exchange: form.exchange || "NS" } });
        setLiveLtp(data);
      } catch (_e) {
        setLiveLtp({ error: true });
      } finally {
        setLtpLoading(false);
      }
    }, 500);
    return () => { clearTimeout(handle); setLtpLoading(false); };
  }, [open, form.stock, form.exchange]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (precheck) setPrecheck(null); };

  const isDerivative = DERIVATIVES.includes(form.category);
  const derivedQty = isDerivative && form.lots !== "" && form.lot_size !== "" ? Number(form.lots) * Number(form.lot_size) : null;
  const effectiveQty = isDerivative ? derivedQty : toNum(form.quantity);
  const lotFields = {
    quantity: effectiveQty ?? 0,
    lots: isDerivative ? toNum(form.lots) : null,
    lot_size: isDerivative ? toNum(form.lot_size) : null,
  };

  async function runPrecheck() {
    setPrechecking(true);
    setPrecheck(null);
    try {
      const payload = {
        stock: form.stock,
        broker: form.broker,
        category: form.category,
        transaction: form.transaction,
        rate: Number(form.rate),
        ...lotFields,
        stop_loss: form.stop_loss === "" ? null : Number(form.stop_loss),
        target: form.target === "" ? null : Number(form.target),
        emotion: form.emotion || null,
        notes: form.notes || null,
      };
      const { data } = await api.post("/trades/precheck", payload);
      setPrecheck(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Pre-check failed");
    } finally {
      setPrechecking(false);
    }
  }

  async function save() {
    setBusy(true);
    setErr("");
    const payload = {
      ...form,
      rate: Number(form.rate),
      ...lotFields,
      stop_loss: form.stop_loss === "" ? null : Number(form.stop_loss),
      target: form.target === "" ? null : Number(form.target),
      exit_rate: form.exit_rate === "" ? null : Number(form.exit_rate),
      // Normalize empty-string dates to null so backend $ifNull sort works correctly
      exit_date: form.exit_date === "" ? null : form.exit_date,
      date_of_purchase: form.date_of_purchase === "" ? null : form.date_of_purchase,
    };
    try {
      if (isEdit) {
        await api.put(`/trades/${initial.id}`, payload);
        toast.success("Trade updated");
      } else {
        await api.post("/trades", payload);
        toast.success("Trade added");
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!reference) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="trade-dialog"
        className="bg-[#121318] border-[#27272A] text-white max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-xs tracking-[0.25em] uppercase text-[#A1A1AA]">
            {isEdit ? "Edit Trade" : "New Trade"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Broker">
            <SelectRow value={form.broker} onChange={(v) => set("broker", v)} options={reference.brokers} testid="trade-broker" />
          </Field>
          <Field label="Category">
            <SelectRow value={form.category} onChange={(v) => set("category", v)} options={reference.categories} testid="trade-category" />
          </Field>
          <Field label="Stock Symbol">
            <div className="relative">
              <Input data-testid="trade-stock" value={form.stock} onChange={(e) => set("stock", e.target.value.toUpperCase())}
                placeholder="RELIANCE" className="bg-[#08080A] border-[#27272A] text-white rounded-sm uppercase pr-24" />
              {form.stock && form.stock.length >= 2 && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1" data-testid="ltp-inline">
                  {ltpLoading ? (
                    <span className="text-[10px] text-[#71717A] tracking-wider">…</span>
                  ) : liveLtp?.last ? (
                    <button type="button"
                      data-testid="use-ltp-btn"
                      onClick={() => set("rate", String(liveLtp.last))}
                      title="Use LTP as entry rate"
                      className={`num text-[11px] px-1.5 py-0.5 rounded-sm border ${liveLtp.change_pct >= 0 ? "text-[#00C805] border-[#00C805]/40 bg-[#00C805]/10" : "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10"} hover:brightness-125`}>
                      ₹{liveLtp.last}
                    </button>
                  ) : liveLtp?.error ? (
                    <span className="text-[10px] text-[#FFB800] tracking-wider">N/A</span>
                  ) : null}
                </div>
              )}
            </div>
          </Field>
          <Field label="Exchange">
            <SelectRow value={form.exchange} onChange={(v) => set("exchange", v)} options={["NS", "BO"]} testid="trade-exchange" />
          </Field>
          <Field label="Transaction">
            <SelectRow value={form.transaction} onChange={(v) => set("transaction", v)} options={reference.transactions} testid="trade-transaction" />
          </Field>
          <Field label="Date">
            <Input data-testid="trade-date" type="date" value={form.date_of_purchase || ""} onChange={(e) => set("date_of_purchase", e.target.value)}
              className="bg-[#08080A] border-[#27272A] text-white rounded-sm" />
          </Field>
          <Field label="Rate">
            <Input data-testid="trade-rate" type="number" step="0.01" value={form.rate} onChange={(e) => set("rate", e.target.value)}
              className="bg-[#08080A] border-[#27272A] text-white rounded-sm num" />
          </Field>
          {isDerivative ? (
            <>
              <Field label="Lots">
                <Input data-testid="trade-lots" type="number" step="1" min="1" value={form.lots} onChange={(e) => set("lots", e.target.value)}
                  placeholder="2" className="bg-[#08080A] border-[#27272A] text-white rounded-sm num" />
              </Field>
              <Field label="Lot Size">
                <div className="relative">
                  <Input data-testid="trade-lot-size" type="number" step="1" min="1" value={form.lot_size}
                    onChange={(e) => { setLotHint(""); set("lot_size", e.target.value); }}
                    placeholder="e.g. 75 for NIFTY" className="bg-[#08080A] border-[#27272A] text-white rounded-sm num pr-28" />
                  {lotHint && (
                    <span data-testid="lot-size-hint" className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] tracking-wider uppercase text-[#007AFF] truncate max-w-[110px]" title={lotHint}>
                      {lotHint}
                    </span>
                  )}
                </div>
              </Field>
              <Field label="Quantity (Lots × Lot Size)">
                <div data-testid="trade-quantity-derived"
                  className="h-10 flex items-center px-3 bg-[#08080A]/60 border border-dashed border-[#27272A] text-[#A1A1AA] rounded-sm num text-sm">
                  {derivedQty != null && !Number.isNaN(derivedQty) ? `${derivedQty} contracts` : "—"}
                </div>
              </Field>
            </>
          ) : (
            <Field label="Quantity">
              <Input data-testid="trade-quantity" type="number" step="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)}
                className="bg-[#08080A] border-[#27272A] text-white rounded-sm num" />
            </Field>
          )}
          <Field label="Stop Loss">
            <Input data-testid="trade-stoploss" type="number" step="0.01" value={form.stop_loss} onChange={(e) => set("stop_loss", e.target.value)}
              className="bg-[#08080A] border-[#27272A] text-white rounded-sm num" />
          </Field>
          <Field label="Target">
            <Input data-testid="trade-target" type="number" step="0.01" value={form.target} onChange={(e) => set("target", e.target.value)}
              className="bg-[#08080A] border-[#27272A] text-white rounded-sm num" />
          </Field>
          <Field label="Status">
            <SelectRow value={form.status} onChange={(v) => set("status", v)} options={reference.statuses} testid="trade-status" />
          </Field>
          {form.status === "CLOSED" && (
            <>
              <Field label="Exit Rate">
                <Input data-testid="trade-exitrate" type="number" step="0.01" value={form.exit_rate} onChange={(e) => set("exit_rate", e.target.value)}
                  className="bg-[#08080A] border-[#27272A] text-white rounded-sm num" />
              </Field>
              <Field label="Exit Date">
                <Input data-testid="trade-exitdate" type="date" value={form.exit_date || ""} onChange={(e) => set("exit_date", e.target.value)}
                  className="bg-[#08080A] border-[#27272A] text-white rounded-sm" />
              </Field>
            </>
          )}
          <div className="col-span-2">
            <Field label="Emotion (optional — helps AI coach)">
              <div className="flex flex-wrap gap-2" data-testid="emotion-row-dialog">
                {EMOTIONS.map((e) => {
                  const active = form.emotion === e;
                  return (
                    <button
                      key={e}
                      type="button"
                      data-testid={`trade-emotion-${e}`}
                      onClick={() => set("emotion", active ? "" : e)}
                      className={`px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-semibold rounded-sm border transition-colors ${
                        active
                          ? "border-[#007AFF] text-[#007AFF] bg-[#007AFF]/10"
                          : "border-[#27272A] text-[#71717A] hover:text-white hover:border-[#3F3F46]"
                      }`}
                    >
                      {e}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Reason / Notes">
              <Textarea data-testid="trade-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                className="bg-[#08080A] border-[#27272A] text-white rounded-sm" placeholder="Why this trade…" />
            </Field>
          </div>
        </div>

        {/* Pre-Trade Checklist */}
        {!isEdit && (
          <PreCheckPanel
            precheck={precheck}
            prechecking={prechecking}
            onRun={runPrecheck}
            canRun={!!form.stock && !!form.rate && !!effectiveQty}
          />
        )}

        {err && <div data-testid="trade-error" className="text-xs text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/20 px-3 py-2 rounded-sm">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <Button data-testid="trade-cancel" variant="outline" onClick={onClose}
            className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm">Cancel</Button>
          <Button data-testid="trade-save" onClick={save} disabled={busy}
            className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-sm">
            {busy ? "Saving…" : isEdit ? "Update Trade" : "Add Trade"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-[#71717A]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SelectRow({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-[#08080A] border-[#27272A] text-white rounded-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[#121318] border-[#27272A] text-white rounded-sm">
        {options.map((o) => (
          <SelectItem key={o} value={o} className="text-white focus:bg-[#1A1B23]">{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


const VERDICT_STYLES = {
  GO: {
    color: "#00C805",
    bg: "#00C805/10",
    Icon: CheckCircle2,
    label: "GO",
  },
  CAUTION: {
    color: "#FFB800",
    bg: "#FFB800/10",
    Icon: AlertTriangle,
    label: "CAUTION",
  },
  WARNING: {
    color: "#FF3B30",
    bg: "#FF3B30/10",
    Icon: AlertOctagon,
    label: "WARNING",
  },
};

function PreCheckPanel({ precheck, prechecking, onRun, canRun }) {
  const style = precheck?.verdict ? VERDICT_STYLES[precheck.verdict] : null;
  return (
    <div className="border border-[#27272A] bg-[#08080A] rounded-sm p-3" data-testid="precheck-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-3.5 w-3.5 text-[#007AFF]" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#007AFF] font-semibold">
            Pre-Trade Checklist
          </span>
        </div>
        <Button
          type="button"
          data-testid="precheck-btn"
          size="sm"
          onClick={onRun}
          disabled={!canRun || prechecking}
          className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-sm h-7 text-[10px] tracking-wider font-semibold disabled:opacity-40"
        >
          {prechecking ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-2" />
              Checking…
            </>
          ) : precheck ? (
            "Re-Check"
          ) : (
            "Check with AI"
          )}
        </Button>
      </div>
      {precheck && style ? (
        <div className="mt-3 space-y-2" data-testid="precheck-result">
          <div className="flex items-center gap-2">
            <style.Icon className="h-4 w-4" style={{ color: style.color }} />
            <span className="text-sm font-semibold tracking-wider" style={{ color: style.color }} data-testid="precheck-verdict">
              {style.label}
            </span>
            {precheck.risk_reward != null && (
              <span className="text-[10px] text-[#71717A] num ml-auto">
                R:R = {precheck.risk_reward} · {precheck.history_count} similar trades
              </span>
            )}
          </div>
          {precheck.reason && (
            <div className="text-xs text-[#F8F9FA] leading-relaxed" data-testid="precheck-reason">
              {precheck.reason}
            </div>
          )}
          {precheck.suggestion && (
            <div className="text-xs text-[#A1A1AA] italic" data-testid="precheck-suggestion">
              → {precheck.suggestion}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-2 text-[10px] text-[#71717A]">
          {canRun
            ? "Claude will review this trade against your history of similar setups before you commit."
            : "Fill Stock, Rate and Quantity (or Lots × Lot Size) to enable the AI check."}
        </div>
      )}
    </div>
  );
}
